// /app/frontend/src/hooks/useNotificationsPolling.js
//
// Polls `/notifications/unread-count` on a low cadence (30s) so the bell's
// green dot stays fresh without flooding the backend. Pauses when the tab
// is hidden and immediately refreshes on Visibility→visible.
//
// Deliberately NOT a global provider — the bell owns this hook. If future
// features need the count too, hoist it into a context or SWR key.

import { useEffect, useRef, useState, useCallback } from "react";
import { fetchUnreadCount } from "../lib/notificationsApi";

const POLL_MS = 30_000;

export default function useNotificationsPolling({ enabled = true } = {}) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const alive = useRef(true);

  const refresh = useCallback(async () => {
    if (!enabled) return 0;
    try {
      const n = await fetchUnreadCount();
      if (alive.current) {
        setUnreadCount(n);
        setError(null);
      }
      return n;
    } catch (e) {
      if (alive.current) setError(e);
      return 0;
    }
  }, [enabled]);

  useEffect(() => {
    alive.current = true;
    if (!enabled) return () => {};
    refresh();
    const tick = () => {
      // Only poll while the tab is visible — saves battery on backgrounded
      // PWAs and stops the API log from filling with cron-like traffic.
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        refresh();
      }
    };
    timerRef.current = setInterval(tick, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      alive.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [enabled, refresh]);

  // Optimistic local decrement — lets the bell dot vanish instantly when a
  // notification is marked read without waiting for the next poll.
  const decrement = useCallback((by = 1) => {
    setUnreadCount((prev) => Math.max(0, prev - by));
  }, []);

  const setCount = useCallback((n) => setUnreadCount(Math.max(0, Number(n) || 0)), []);

  return { unreadCount, error, refresh, decrement, setCount };
}
